import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { ErrorHandlerService } from '../error-handler.service';
import { AppConfigService } from '../app-config.service';
import { AutoRedactionJob } from '../../models/auto-redaction-job.model';
import { AutoRedactionRule } from '../../models/auto-redaction-rule.model';
import { ODataModelSanitizerService } from '../odata-model-sanitizer.service';

@Injectable({
	providedIn: 'root'
})
export class AutoRedactionService {
	constructor(private http: HttpClient, private errorHandler: ErrorHandlerService, private appConfigService: AppConfigService, private sanitizer: ODataModelSanitizerService) { }

	public startAutoRedactionJobWorkflow(autoRedactionJobId: string): Observable<void> {
		return this.http.post<void>(`${this.appConfigService.getConfig().webApi}/api2/AutoRedactionJobs/${autoRedactionJobId}/workflow`, null)
			.pipe(catchError(this.errorHandler.handleError<void>(`start Auto Redaction job`)));
	}

	public getAutoRedactionJobs(): Observable<AutoRedactionJob[]> {
		return this.http.get<AutoRedactionJob[]>(`${this.appConfigService.getConfig().webApi}/api2/AutoRedactionJobs`)
			.pipe(
				tap(odl => odl.forEach(j => this.sanitizer.sanitizeJob(j))),
				catchError(this.errorHandler.handleError<AutoRedactionJob[]>(`get Auto Redactions`)));
	}

	public createAutoRedactionJob(autoRedactionJob: AutoRedactionJob): Observable<AutoRedactionJob> {
		return this.http.post<AutoRedactionJob>(`${this.appConfigService.getConfig().webApi}/api2/AutoRedactionJobs`, autoRedactionJob)
			.pipe(
				tap(j => {
					this.sanitizer.sanitizeJob(j);
					j.autoRedactionRules = new Array<AutoRedactionRule>();
				}),
				catchError(this.errorHandler.handleError<AutoRedactionJob>(`post Auto Redaction Id ${autoRedactionJob.title}`))
			);
	}

	public updateAutoRedactionJob(autoRedactionJob: AutoRedactionJob): Observable<AutoRedactionJob> {
		const rules = autoRedactionJob.autoRedactionRules;
		return this.http.put<AutoRedactionJob>(`${this.appConfigService.getConfig().webApi}/api2/AutoRedactionJobs/${autoRedactionJob.id}`, autoRedactionJob)
			.pipe(
				tap(j => {
					this.sanitizer.sanitizeJob(j);
					j.autoRedactionRules = rules;
				}),
				catchError(this.errorHandler.handleError<AutoRedactionJob>(`put Auto Redaction ${autoRedactionJob.id}`)));
	}

	public deleteAutoRedactionJob(autoRedactionJobId: string): Observable<boolean> {
		return this.http.delete<boolean>(`${this.appConfigService.getConfig().webApi}/api2/AutoRedactionJobs/${autoRedactionJobId}`)
			.pipe(catchError(this.errorHandler.handleError<boolean>(`delete Auto Redaction Id ${autoRedactionJobId}`)));
	}

	public createAutoRedactionRule(autoRedactionRule: AutoRedactionRule): Observable<AutoRedactionRule> {
		return this.http.post<AutoRedactionRule>(`${this.appConfigService.getConfig().webApi}/api2/AutoRedactionRules`, autoRedactionRule);
	}

	public updateAutoRedactionRule(autoRedactionRule: AutoRedactionRule): Observable<AutoRedactionRule> {
		return this.http.put<AutoRedactionRule>(`${this.appConfigService.getConfig().webApi}/api2/AutoRedactionRules/${autoRedactionRule.id}`, autoRedactionRule);
	}

	public deleteAutoRedactionRule(autoRedactionQueryId: string): Observable<boolean> {
		return this.http.delete<boolean>(`${this.appConfigService.getConfig().webApi}/api2/AutoRedactionRules/${autoRedactionQueryId}`)
			.pipe(catchError(this.errorHandler.handleError<boolean>(`delete Auto Redaction Query Id ${autoRedactionQueryId}`)));
	}
}
